import React, { PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { Theme } from '@routific/hive';
import PanelCollapsed from './PanelCollapsed';
import { breakpoints, ScreenSizes } from '../../../helpers/breakpoints';
import PanelHeader from './PanelHeader';
import { PANEL_WIDTH } from '../../../constants';
import PanelFooter, { JustifyContent } from './PanelFooter';

interface Props extends PropsWithChildren {
  title: ReactNode | string;
  titlePrefix?: ReactNode;
  label?: string;
  mode: PanelMode;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  collapsedContent?: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  onBack?: () => void;
  zIndex?: number;
  dataCy?: string;
  dataTestId?: string;
  bordered?: boolean;
  fixedWidth?: boolean;
  side?: string;
  loading?: boolean;
  panelPadding?: number;
  style?: React.CSSProperties;
  footerAlignment?: JustifyContent;
  disableBack?: boolean;
}

export enum PanelMode {
  COLLAPSED = 'collapsed',
  OPEN = 'open',
  EXPANDED = 'expanded',
}

export const COLLAPSED_PANEL_WIDTH_PIXELS = 80;
export const OPEN_PANEL_MIN_WIDTH_PIXELS = PANEL_WIDTH;

const CUBIC_BEZIER = 'ease-in-out';

const Inner = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const getPanelWidth = (fixedWidth: boolean | undefined = false) => (fixedWidth ? `${PANEL_WIDTH}px` : '22vw');

const handleContainerWidth = (type: 'container' | 'toggle' | 'inner', mode: PanelMode, fixedWidth = false) => {
  switch (mode) {
    case PanelMode.EXPANDED: {
      return { min: 'calc(70vw)', max: 'calc(70vw)' };
    }
    case PanelMode.OPEN: {
      return type === 'toggle' ? { max: '0px' } : { min: `${OPEN_PANEL_MIN_WIDTH_PIXELS}px`, max: getPanelWidth(fixedWidth) };
    }
    case PanelMode.COLLAPSED:
    default: {
      return type === 'inner'
        ? { min: `${OPEN_PANEL_MIN_WIDTH_PIXELS}px`, max: '22vw' }
        : { min: `${COLLAPSED_PANEL_WIDTH_PIXELS}px`, max: `${COLLAPSED_PANEL_WIDTH_PIXELS}px` };
    }
  }
};

const Container = styled.div<{ zIndex?: number, mode: PanelMode, bordered?: boolean, fixedWidth?: boolean, panelPadding: number }>`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  flex: 1 1;
  justify-content: flex-end;
  width: 100%;
  min-height: 0;
  height: 100%;
  transition: all .1s ${CUBIC_BEZIER};
  background-color: ${Theme.Colors.gray0};
  box-shadow: ${({ bordered }) => (bordered ? 'none' : '0 2px 4px 0 rgba(0, 0, 0, 0.25)')};
  border: ${({ bordered }) => (bordered ? `1px solid ${Theme.Colors.gray4}` : 'none')};
  ${({ mode, zIndex }) => breakpoints('lg')(`
    max-width: 100%;
    width: 100%;
    align-items: flex-start;
    z-index: ${zIndex ?? '9'};
    &:hover {
     ${mode === PanelMode.COLLAPSED && 'cursor: pointer;'}
    }
    .panel-collapsed {
      transition: max-width .1s ${CUBIC_BEZIER}, opacity .1s ${CUBIC_BEZIER}, flex-basis .1s ${CUBIC_BEZIER}; width: 100%;
      overflow: hidden;
      opacity: ${mode === PanelMode.COLLAPSED ? '1' : '0'};
      width: 100%;
      max-width: 100%;
      flex-shrink: 0;
      flex-grow: 0;
      flex-basis: ${handleContainerWidth('toggle', mode).max};
    }
    ${Inner} {
      transition: all .1s ${CUBIC_BEZIER};
      max-width: 100%;
      width: 100%;

      &> div > *:not(.panel-collapsed) {
        transition: all .1s ${CUBIC_BEZIER};

        ${mode === PanelMode.COLLAPSED
    ? 'visibility: hidden; opacity:0;'
    : 'visibility: visible; opacity: 1'}
      }
    }
  `)}
`;

const Panel: React.FC<Props> = ({
  mode,
  onOpen,
  onClose,
  onBack,
  collapsedContent,
  headerContent,
  footerContent,
  children,
  label,
  zIndex,
  title,
  titlePrefix,
  bordered,
  dataCy,
  dataTestId,
  fixedWidth,
  side,
  loading,
  panelPadding,
  style,
  footerAlignment,
  disableBack = false,
}) => {
  const isBigScreen = useMediaQuery({ minWidth: ScreenSizes.lg });

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onOpen) {
      onOpen();
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClose) {
      onClose();
    }
  };

  const handleBack = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onBack) {
      onBack();
    }
  };

  const closeHandler = onClose ? handleClose : undefined;
  const backHandler = onBack ? handleBack : undefined;

  return (
    <div style={{ height: '100%' }}>
      <Container zIndex={zIndex} mode={mode} data-cy={dataCy} data-testid={dataTestId} bordered={bordered} fixedWidth={fixedWidth} panelPadding={panelPadding || 0} style={style}>
        {isBigScreen && collapsedContent && mode === PanelMode.COLLAPSED
          && (
            <PanelCollapsed label={label} onClick={handleOpen} data-cy="panel-collapsed">
              {collapsedContent}
            </PanelCollapsed>
          )}
        <Inner>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <PanelHeader
              title={title}
              titlePrefix={titlePrefix}
              content={headerContent}
              loading={loading}
              onClose={closeHandler}
              onBack={backHandler}
              side={side}
              disableBack={disableBack}
            />
            {children}
            <PanelFooter visible={!!footerContent} footerAlignment={footerAlignment}>{footerContent}</PanelFooter>
          </div>
        </Inner>
      </Container>
    </div>
  );
};

export default Panel;
